/* @flow */

import React from 'react';
import { connect } from 'react-redux';

import Box from '@material-ui/core/Box';

import moment from 'moment';
import LoadingComponent from 'components/loading';
import IntlMessageComponent from 'components/formatters/intlMessage';

import SellerReportFiltersComponent from './filters';
import SellerReportTableComponent from './table';

import Filter from 'models/isos/filters/filter';
import FilterCompany from 'models/isos/filters/filterCompany';
import FilterOffice from 'models/isos/filters/filterOffice';
import SellerReport from 'models/isos/sellers/sellerReport';
import SellerReportFilters from 'models/isos/sellers/sellerReportFilters';

import DownloadSellerReportRequest from 'models/requests/isos/downloadSellerReportRequest';

import IsoService from 'services/IsoService';

type ISOSellersComponentProps = {
  activeCompanyId: number;
}

type ISOSellersComponentState = {
  filters: SellerReportFilters;
  isLoading: boolean;
  sellers: SellerReport[];
  sellerCompanyName: string;
}


class ISOSellersComponent extends React.Component<ISOSellersComponentProps, ISOSellersComponentState> {

  constructor(props) {
    super(props);
    this.state = {
      filters: null,
      isLoading: true,
      sellers: null,
      sellerCompanyName: null,
    };
  }

  componentDidMount = async () => {
    try {
      const sellers = await IsoService.getSellerReport(this.props.activeCompanyId);
      const filters = this.createFilterModel(sellers);
      this.setState({
        filters,
        isLoading: false,
        sellers
      });
    } catch (e) {
      console.error(e);
    }
  }

  getFilteredData = (ignoreSellerCompanies: boolean) => {
    let data = [];
    // Apply company/office/person filtering
    const filters = this.filterToObject(ignoreSellerCompanies);

    // clone from state so we can modify data as we show it
    let sellersToUse = structuredClone(this.state.sellers);
    data = this.multiFilter(sellersToUse, filters);

    // Apply expired filtering
    if (this.state.filters.expired.find(f => !f.isSelected) !== undefined) {
      if (this.state.filters.expired.find(f => f.id == 1).isSelected) {
        data = data.filter((s) => {
          if (moment(s.membershipExpiration).isBefore(moment(), 'day'))
            return true;
        });
      }
      if (this.state.filters.expired.find(f => f.id == 0).isSelected) {
        data = data.filter((s) => {
          if (moment(s.membershipExpiration).isAfter(moment(), 'day') || moment(s.membershipExpiration).isSame(moment(), 'day'))
            return true;
        });
      }
    }

    // Group together companies if no iso filter used
    if (filters.filterISOId.length === 0 && filters.filterISOOfficeId.length === 0 && filters.filterISOPersonId.length === 0)
    {
      // Get Grouped companies and the ones that exist multiple times in array
      let groupedByCompany = Object.entries(this.groupBy(data, 'sellerCompany'));
      let multipleCompanies = groupedByCompany.filter(s => s[1].length > 1);
      
      multipleCompanies.forEach(companyArray => {
        // get the actual array of sellers
        let sellers = companyArray[1];
        // use first seller to modify data
        let firstSeller = sellers[0];
        firstSeller.financedAmount = sellers.reduce((acc, curr) => acc + curr.financedAmount, 0)
        firstSeller.firstInvoiceCreated = new Date(Math.min(...sellers.map(e => new Date(e.firstInvoiceCreated))));
        firstSeller.lastInvoiceCreated = new Date(Math.max(...sellers.map(e => new Date(e.lastInvoiceCreated))));
        firstSeller.firstInvoiceFinanced = new Date(Math.min(...sellers.map(e => new Date(e.firstInvoiceFinanced))));
        firstSeller.lastInvoiceFinanced = new Date(Math.max(...sellers.map(e => new Date(e.lastInvoiceFinanced))));

        // remove other same sellers
        sellers.forEach(seller => {
          if (seller === sellers[0])
            return;
          let indexToRemove = data.indexOf(seller);
          data.splice(indexToRemove, 1);
        })
      });
    }

    return data;
  }

  groupBy = (xs: array, key: string) =>  {
    return xs.reduce(function(rv, x) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  }

  createFilterModel = (data: any) => {
    const filters = new SellerReportFilters();

    for (let i = 0; i < data.length; i++) {
      let isoCompany = filters.companies.find(c => c.id === data[i].filterISOId);
      if (isoCompany === undefined) {
        let isoCompanyName = data[i].isoCompany.length === 0 ? <IntlMessageComponent id="general.filters.unspecifiedcompany" /> : data[i].isoCompany;
        isoCompany = new FilterCompany(data[i].filterISOId, isoCompanyName);
        filters.companies.push(isoCompany);
      }

      let isoOffice = isoCompany.offices.find(o => o.id === data[i].filterISOOfficeId);
      if (isoOffice === undefined) {
        let isoOfficeName = data[i].isoOffice.length === 0 ? <IntlMessageComponent id="general.filters.unspecifiedoffice" /> : data[i].isoOffice;
        isoOffice = new FilterOffice(data[i].filterISOOfficeId, isoOfficeName);
        isoCompany.offices.push(isoOffice);
      }

      let isoPerson = isoOffice.persons.find(p => p.id === data[i].filterISOPersonId);
      if (isoPerson === undefined) {
        let isoPersonName = data[i].isoPerson.length === 0 ? <IntlMessageComponent id="general.filters.unspecifiedperson" /> : data[i].isoPerson;
        isoPerson = new Filter(data[i].filterISOPersonId, isoPersonName);
        isoOffice.persons.push(isoPerson);
      }

      let sellerCompany = filters.sellerCompanies.find(sc => sc.id === data[i].filterSellerCompanyId);
      if (sellerCompany === undefined) {
        sellerCompany = new Filter(data[i].filterSellerCompanyId, data[i].sellerCompany);
        filters.sellerCompanies.push(sellerCompany);
      }
    }

    filters.sellerCompanies = filters.sellerCompanies.sort((a, b) => (a.name < b.name ? -1 : 1));

    // expired - Hardcoded filter
    filters.expired.push(new Filter(1, <IntlMessageComponent id="general.iso.seller.expired.yes" />));
    filters.expired.push(new Filter(0, <IntlMessageComponent id="general.iso.seller.expired.no" />));

    return filters;
  }

  filterToObject = (ignoreSellerCompanies: boolean) => {
    const filters = {
      filterISOId: [],
      filterISOOfficeId: [],
      filterISOPersonId: [],
      filterSellerCompanyId: []
    };

    for (let i = 0; i < this.state.filters.companies.length; i++) {
      if (this.state.filters.companies[i].isSelected) {
        filters.filterISOId.push(this.state.filters.companies[i].id);
      }

      for (let j = 0; j < this.state.filters.companies[i].offices.length; j++) {
        if (this.state.filters.companies[i].offices[j].isSelected) {
          filters.filterISOOfficeId.push(this.state.filters.companies[i].offices[j].id);
        }

        for (let k = 0; k < this.state.filters.companies[i].offices[j].persons.length; k++) {
          if (this.state.filters.companies[i].offices[j].persons[k].isSelected) {
            filters.filterISOPersonId.push(this.state.filters.companies[i].offices[j].persons[k].id);
          }
        }
      }
    }

    if (!ignoreSellerCompanies) {
      for (let i = 0; i < this.state.filters.sellerCompanies.length; i++) {
        if (this.state.filters.sellerCompanies[i].isSelected) {
          filters.filterSellerCompanyId.push(this.state.filters.sellerCompanies[i].id);
        }
      }
    }

    return filters;
  }

  multiFilter = (array, filters) => {
    const filterKeys = Object.keys(filters);
    // filters all elements passing the criteria
    return array.filter(item => filterKeys.every((key) => {
      // ignores an empty filter
      if (!filters[key].length) return true;
      return filters[key].includes(item[key]);
    }));
  }

  selectFilter = (filterId: string, optionId: number) => {
    const filters = this.state.filters;
    for (let i = 0; i < filters[filterId].length; i++) {
      if (filters[filterId][i].id === optionId) {
        filters[filterId][i].isSelected = !filters[filterId][i].isSelected;
      }
    }
    this.setState({ filters });
  }

  selectFilterISO = (filterId: string, optionId: number) => {
    const filters = this.state.filters;

    if (filterId === 'company') {
      for (let i = 0; i < filters.companies.length; i++) {
        if (filters.companies[i].id === optionId) {
          filters.companies[i].isSelected = !filters.companies[i].isSelected;

          if (!filters.companies[i].isSelected) {
            // if we unselect a company, unselect all its offices & persons
            for (let j = 0; j < filters.companies[i].offices.length; j++) {
              filters.companies[i].offices[j].isSelected = false;
              for (let k = 0; k < filters.companies[i].offices[j].persons.length; k++) {
                filters.companies[i].offices[j].persons[k].isSelected = false;
              }
            }
          }
        }
      }
    }

    if (filterId === 'office') {
      for (let i = 0; i < filters.companies.length; i++) {
        for (let j = 0; j < filters.companies[i].offices.length; j++) {
          if (filters.companies[i].offices[j].id === optionId) {
            filters.companies[i].offices[j].isSelected = !filters.companies[i].offices[j].isSelected;

            if (filters.companies[i].offices[j].isSelected) {
              // if we select an office, select its company
              filters.companies[i].isSelected = true;
            } else {
              // if we unselect an office, unselect all its persons
              for (let k = 0; k < filters.companies[i].offices[j].length; k++) {
                filters.companies[i].offices[j].persons[k].isSelected = false;
              }
            }
          }
        }
      }
    }

    if (filterId === 'person') {
      for (let i = 0; i < filters.companies.length; i++) {
        for (let j = 0; j < filters.companies[i].offices.length; j++) {
          for (let k = 0; k < filters.companies[i].offices[j].persons.length; k++) {
            if (filters.companies[i].offices[j].persons[k].id === optionId) {
              filters.companies[i].offices[j].persons[k].isSelected = !filters.companies[i].offices[j].persons[k].isSelected;

              if (filters.companies[i].offices[j].persons[k].isSelected) {
                // if we select a person, select its company and office
                filters.companies[i].isSelected = true;
                filters.companies[i].offices[j].isSelected = true;
              }
            }
          }
        }
      }
    }

    //Unselect all seller companies to be able to work with available seller companies to select updating automatically
    for (let i = 0; i < filters.sellerCompanies.length; i++) {
      filters.sellerCompanies[i].isSelected = false;
    }

    this.setState({ filters });
  }

  downloadSellerReport = async (format: string) => {
    try {
      const request = new DownloadSellerReportRequest(this.props.activeCompanyId, format);

      for (let i = 0; i < this.state.filters.companies.length; i++) {
        if (this.state.filters.companies[i].isSelected) {
          request.isoCompanyIds.push(this.state.filters.companies[i].id);
        }

        for (let j = 0; j < this.state.filters.companies[i].offices.length; j++) {
          if (this.state.filters.companies[i].offices[j].isSelected) {
            request.isoOfficeIds.push(this.state.filters.companies[i].offices[j].id);
          }

          for (let k = 0; k < this.state.filters.companies[i].offices[j].persons.length; k++) {
            if (this.state.filters.companies[i].offices[j].persons[k].isSelected) {
              request.isoPersonIds.push(this.state.filters.companies[i].offices[j].persons[k].id);
            }
          }
        }
      }

      this.state.filters.sellerCompanies.forEach(filter => {
        if (filter.isSelected) request.sellerCompanyIds.push(filter.id);
      });

      this.state.filters.expired.forEach(filter => {
        if (filter.isSelected) {
          if (filter.id === 0) request.expireds.push('No');
          else request.expireds.push('Yes');
        }
      });

      await IsoService.downloadSellerReport(request);
    } catch (e) {
      console.error(e);
    }
  }

  clearFilters = () => {
    if (this.state.filters === null) return;

    const filters = this.state.filters;

    for (let i = 0; i < filters.companies.length; i++) {
      filters.companies[i].isSelected = false;
      for (let j = 0; j < filters.companies[i].offices.length; j++) {
        filters.companies[i].offices[j].isSelected = false;
        for (let k = 0; k < filters.companies[i].offices[j].persons.length; k++) {
          filters.companies[i].offices[j].persons[k].isSelected = false;
        }
      }
    }

    for (let i = 0; i < filters.sellerCompanies.length; i++) {
      filters.sellerCompanies[i].isSelected = false;
    }

    this.setState({ filters, sellerCompanyName: '' });
  }

  sellerCompanyNameChange = (name: string) => {
    this.setState({ sellerCompanyName: name });
  }

  render() {
    if (this.state.isLoading) {
      return (<LoadingComponent />);
    }

    return (
      <React.Fragment>
        <SellerReportFiltersComponent
          filters={this.state.filters}
          selectFilter={this.selectFilter}
          selectFilterISO={this.selectFilterISO}
          sellerCompanyNameChange={this.sellerCompanyNameChange}
          clearFilters={this.clearFilters}
          downloadSellerReport={this.downloadSellerReport}
          sellerCompanyName={this.state.sellerCompanyName}
          availableSellerCompanyNames={this.getFilteredData(true).map(item => item.sellerCompany).filter((value, index, self) => self.indexOf(value) === index)}
        />


        <Box mt={4} data-walkme="isosellers-walkthru-step4">
          <SellerReportTableComponent sellers={this.getFilteredData()} />
        </Box>
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({ auth }) => ({
  activeCompanyId: auth.user.activeCompany.id
});

export default connect(mapStateToProps)(ISOSellersComponent);
